import {
  isSelection,
  sameValues
} from 'modules/candidate-profile/tables/filters/candidateFilterState';
import {
  CandidateFilterMatchMode,
  CandidateFilterState
} from 'modules/candidate-profile/types/CandidateSearchFilter';

/**
 * A filter the recruiter removed. The assistant derives its filters from the
 * whole conversation on every turn, and may not add a dismissed one back.
 *
 * `mode` and `values` narrow it where one field carries more than one filter,
 * so the filter the recruiter kept is not dismissed with it.
 *
 * `removedValues` are the values the recruiter took off. Only adding all of
 * them back by hand undoes the dismissal; adding a different value does not.
 */
export type DismissedFilter = {
  field: string;
  mode?: CandidateFilterMatchMode;
  values?: string[];
  removedValues?: string[];
};

const modeOf = (condition: CandidateFilterState) =>
  'mode' in condition ? condition.mode : undefined;

// A range, radius or Boolean has no mode, so any mode matches it.
const matchesMode = (
  dismissed: DismissedFilter,
  condition: CandidateFilterState
): boolean =>
  dismissed.mode === undefined ||
  !('mode' in condition) ||
  dismissed.mode === condition.mode;

const matchesValues = (
  dismissed: DismissedFilter,
  condition: CandidateFilterState
): boolean =>
  dismissed.values === undefined ||
  (isSelection(condition) && sameValues(dismissed.values, condition.values));

/**
 * Is this condition one the recruiter dismissed?
 *
 * Matched on the field and not on the id, so a condition matches its dismissal
 * whatever id the parser gives it.
 */
export const isDismissedBy = (
  dismissed: DismissedFilter[],
  condition: CandidateFilterState
): boolean =>
  dismissed.some(
    (each) =>
      each.field === condition.field &&
      matchesMode(each, condition) &&
      matchesValues(each, condition)
  );

export const toDismissedFilter = (
  condition: CandidateFilterState
): DismissedFilter => ({
  field: condition.field,
  ...('mode' in condition ? { mode: condition.mode } : {}),
  ...(isSelection(condition) ? { removedValues: condition.values } : {})
});

/** Does this condition, added by hand, put the dismissed filter back? */
export const isAddedBackBy = (
  dismissed: DismissedFilter,
  condition: CandidateFilterState
): boolean =>
  isDismissedBy([dismissed], condition) &&
  (dismissed.removedValues === undefined ||
    (isSelection(condition) &&
      dismissed.removedValues.every((value) =>
        condition.values.includes(value)
      )));

/**
 * The filters in `before` that `after` no longer has on the same field and
 * mode. Taking one value off a filter leaves the filter standing.
 */
export const removedFilters = (
  before: CandidateFilterState[],
  after: CandidateFilterState[]
): DismissedFilter[] =>
  before
    .filter(
      (condition) =>
        !after.some(
          (each) =>
            each.field === condition.field && modeOf(each) === modeOf(condition)
        )
    )
    .map(toDismissedFilter);
